import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class Cards {
    constructor(query) {
        const el = document.querySelector(query);

        if (!el) return;

        this.el = el;
        this.container = this.el.querySelector(".cards__container");
        this.wrapper = this.el.querySelector(".cards__wrapper");
        this.list = this.el.querySelector(".cards__list");
        this.items = [...this.el.querySelectorAll(".cards__item")];
        this.links = [...this.el.querySelectorAll(".cards__item-link")];
        this.mm = gsap.matchMedia();

        this.tl = null;
        this.st = null;
        this.onResize = null;

        if (!this.container || !this.wrapper || !this.list || !this.items.length) return;

        this.init();
    }

    init() {
        this.initCardFlip();

        this.mm.add("(min-width: 992px)", () => {
            this.onResize = this.handleResize.bind(this);
            window.addEventListener("resize", this.onResize);

            this.setup();

            return () => {
                window.removeEventListener("resize", this.onResize);
                this.onResize = null;
                this.reset();
            };
        });

        this.mm.add("(max-width: 991px)", () => {
            this.reset();

            return () => {
                this.reset();
            };
        });
    }

    setup() {
        this.kill();
        this.clearStyles();

        const leftOffset = this.getLeftOffset();
        const itemHeight = this.getItemsMaxHeight();

        this.list.style.paddingLeft = `${leftOffset}px`;

        for (let i = 0; i < this.items.length; i++) {
            gsap.set(this.items[i], {
                y: i === 0 ? 0 : window.innerHeight * i + 110,
            });
        }

        const fullWidth = this.list.scrollWidth;
        const horizontalShift = Math.max(0, fullWidth - window.innerWidth + leftOffset);
        const maxOffset = this.getMaxOffset();

        this.wrapper.style.height = `${itemHeight}px`;

        const totalDistance = horizontalShift + maxOffset;

        this.container.style.height = `${itemHeight + totalDistance + window.innerHeight / 2}px`;

        this.tl = gsap.timeline({
            defaults: {
                ease: "none",
            },
        });

        for (let i = 0; i < this.items.length; i++) {
            const position = i === 0 ? 0 : window.innerHeight * i + 110;

            // eslint-disable-next-line no-continue
            if (!position) continue;

            this.tl.to(this.items[i], {
                y: 0,
                duration: position,
            }, 0);
        }

        this.tl.to(this.list, {
            x: -horizontalShift,
            duration: totalDistance,
        }, 0);

        this.st = ScrollTrigger.create({
            trigger: this.wrapper,
            start: "center center",
            end: `+=${totalDistance}`,
            scrub: true,
            pin: this.wrapper,
            animation: this.tl,
            invalidateOnRefresh: true,
            anticipatePin: 1,
        });

        ScrollTrigger.refresh();
    }

    getLeftOffset() {
        return Math.max((window.innerWidth - 1390) / 2, 0) + 15;
    }

    getMaxOffset() {
        const lastIndex = this.items.length - 1;

        if (lastIndex <= 0) return 0;

        return window.innerHeight * lastIndex + 110;
    }

    handleResize() {
        if (window.innerWidth < 992) return;
        this.setup();
    }

    kill() {
        if (this.st) {
            this.st.kill();
            this.st = null;
        }

        if (this.tl) {
            this.tl.kill();
            this.tl = null;
        }
    }

    reset() {
        this.kill();
        this.clearStyles();

        ScrollTrigger.refresh();
    }

    destroy() {
        if (this.onResize) {
            window.removeEventListener("resize", this.onResize);
            this.onResize = null;
        }

        this.reset();
        this.mm.revert();
    }

    clearStyles() {
        this.container.style.height = "";
        this.wrapper.style.height = "";
        this.list.style.paddingLeft = "";

        gsap.set(this.list, {
            clearProps: "transform",
        });

        gsap.set(this.items, {
            clearProps: "transform",
        });
    }

    getItemsMaxHeight() {
        let maxHeight = 0;

        this.items.forEach((item) => {
            item.style.height = "";
            maxHeight = Math.max(maxHeight, item.offsetHeight);
        });

        return Math.ceil(maxHeight);
    }

    initCardFlip() {
        this.items.forEach((item) => {
            const frontLink = item.querySelector(".cards__item-front .cards__item-link .icon");
            const backLink = item.querySelector(".cards__item-back .cards__item-link .icon");

            if (frontLink) {
                frontLink.addEventListener("click", () => {
                    item.classList.add("is-flipped");
                });
            }

            if (backLink) {
                backLink.addEventListener("click", () => {
                    item.classList.remove("is-flipped");
                });
            }
        });
    }
}